import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/feature/Sidebar';

interface Notification {
  id: number;
  recipient_uid: string;
  sender_uid: string | null;
  type: string;
  title: string;
  message: string;
  reference_type: string | null;
  reference_id: number | null;
  is_read: number;
  created_at: string;
}

export default function VictimNotificationsPage() {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingId, setMarkingId] = useState<number | null>(null);

  const fetchNotifications = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setError('');

      const response = await fetch(
        `https://guardiancloudcare.com/backend/api/get_notifications.php?uid=${encodeURIComponent(user.uid)}`
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const data = await response.json();

      if (!data?.success) {
        throw new Error(
          data?.message || 'Unable to load notifications.'
        );
      }

      setNotifications(
        Array.isArray(data.notifications)
          ? data.notifications
          : []
      );
    } catch (err: any) {
      console.error(
        'Notifications error:',
        err
      );

      setError(
        err?.message ||
        'Unable to load notifications.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user?.uid]);

  const markAsRead = async (
    notificationId: number
  ) => {
    if (!user?.uid) {
      return;
    }

    setMarkingId(notificationId);
    setError('');

    try {
      const response = await fetch(
        'https://guardiancloudcare.com/backend/api/mark_notification_read.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
            notification_id: notificationId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(
          data?.message ||
          'Unable to mark notification as read.'
        );
      }

      setNotifications(current =>
        current.map(notification =>
          notification.id === notificationId
            ? {
                ...notification,
                is_read: 1,
              }
            : notification
        )
      );
    } catch (err: any) {
      console.error(
        'Mark notification error:',
        err
      );

      setError(
        err?.message ||
        'Unable to update notification.'
      );
    } finally {
      setMarkingId(null);
    }
  };

  const unreadCount =
    notifications.filter(
      notification =>
        Number(notification.is_read) === 0
    ).length;

  const formatDate = (
    value: string
  ) => {
    if (!value) {
      return '';
    }

    const date = new Date(
      value.replace(' ', 'T')
    );

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString(
      undefined,
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }
    );
  };

  const getNotificationIcon = (
    type: string
  ) => {
    switch (type) {
      case 'schedule_created':
        return 'ri-calendar-check-line';

      case 'schedule_updated':
        return 'ri-calendar-edit-line';

      case 'schedule_cancelled':
        return 'ri-calendar-close-line';

      case 'case_update':
        return 'ri-folder-info-line';

      case 'message':
        return 'ri-message-3-line';

      default:
        return 'ri-notification-3-line';
    }
  };

  const getNotificationColor = (
    type: string
  ) => {
    switch (type) {
      case 'schedule_created':
        return 'bg-teal-100 text-teal-600';

      case 'schedule_updated':
        return 'bg-blue-100 text-blue-600';

      case 'schedule_cancelled':
        return 'bg-red-100 text-red-600';

      case 'case_update':
        return 'bg-purple-100 text-purple-600';

      case 'message':
        return 'bg-indigo-100 text-indigo-600';

      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar />

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-teal-100 flex items-center justify-center">
                  <i className="ri-notification-3-line text-xl text-teal-600" />
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-slate-800">
                    Notifications
                  </h1>

                  <p className="text-sm text-slate-500">
                    Stay updated about your case.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-600">
                {notifications.length}{' '}
                {notifications.length === 1
                  ? 'notification'
                  : 'notifications'}
              </span>

              {unreadCount > 0 && (
                <span className="px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-sm font-medium text-red-600">
                  {unreadCount} unread
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5" />

              <div>
                <p className="text-sm font-semibold text-red-700">
                  Unable to load notifications
                </p>

                <p className="text-sm text-red-600 mt-1">
                  {error}
                </p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
              <div className="flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-teal-500 rounded-full animate-spin" />

                <p className="mt-4 text-sm text-slate-500">
                  Loading notifications...
                </p>
              </div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                  <i className="ri-notification-off-line text-3xl text-slate-400" />
                </div>

                <h2 className="mt-5 text-lg font-semibold text-slate-700">
                  No notifications
                </h2>

                <p className="mt-2 max-w-md text-sm text-slate-500">
                  You currently have no notifications.
                  Updates about your case will appear
                  here.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map(notification => {
                const unread =
                  Number(notification.is_read) === 0;

                return (
                  <div
                    key={notification.id}
                    className={`bg-white rounded-2xl border shadow-sm transition-all ${
                      unread
                        ? 'border-teal-200 bg-teal-50/20'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">

                        <div
                          className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(
                            notification.type
                          )}`}
                        >
                          <i
                            className={`${getNotificationIcon(
                              notification.type
                            )} text-xl`}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">

                            <div className="flex items-center gap-2">
                              <h2
                                className={`text-base font-semibold ${
                                  unread
                                    ? 'text-slate-900'
                                    : 'text-slate-700'
                                }`}
                              >
                                {notification.title}
                              </h2>

                              {unread && (
                                <span className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" />
                              )}
                            </div>

                            <span className="text-xs text-slate-400 whitespace-nowrap">
                              {formatDate(
                                notification.created_at
                              )}
                            </span>
                          </div>

                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {notification.message}
                          </p>

                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            {notification.reference_type ===
                              'case_schedule' && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-xs font-medium text-slate-600">
                                <i className="ri-calendar-line" />
                                Case Schedule
                              </span>
                            )}

                            {unread && (
                              <button
                                type="button"
                                disabled={
                                  markingId ===
                                  notification.id
                                }
                                onClick={() =>
                                  markAsRead(
                                    notification.id
                                  )
                                }
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                              >
                                {markingId ===
                                notification.id ? (
                                  <>
                                    <i className="ri-loader-4-line animate-spin" />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-check-line" />
                                    Mark as read
                                  </>
                                )}
                              </button>
                            )}

                            {!unread && (
                              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                                <i className="ri-check-double-line" />
                                Read
                              </span>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 flex items-center justify-center">
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                fetchNotifications();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <i className="ri-refresh-line" />
              Refresh
            </button>
          </div>

        </div>
      </main>
    </div>
  );
}